import { useEffect, useMemo, useState, useContext } from 'react';
import api from '../api';
import { UserContext } from '../context/UserContext';
import MessageSendForm from '../components/MessageSendForm';
import { gameInitials, gameColor, avatarUrlAbs, getGameIconPath, fetchRawgImageUrl, userInitials, userColor } from '../utils/gameIcon';
import AvatarImg from '../components/AvatarImg';

export default function Matchmaking() {
  const { user } = useContext(UserContext);
  const [list, setList] = useState([]);
  const [mine, setMine] = useState([]);
  const [q, setQ] = useState('');
  const [filters, setFilters] = useState({ game: '', platform: '', region: '', status: 'open' });
  const [form, setForm] = useState({ game: '', mode: '', platform: '', region: '', languages: '', skill: '', availability: '', voice: true, crossplay: true, tags: '', description: '', status: 'open' });
  const [editing, setEditing] = useState(null);
  const [contactUser, setContactUser] = useState(null);

  const loadAll = async () => {
    const params = new URLSearchParams();
    if (q) params.set('q', q);
    Object.entries(filters).forEach(([k,v])=>{ if (v) params.set(k, v); });
    const res = await api.get(`/matchmaking?${params.toString()}`);
    setList(res.data.items || []);
  };
  const loadMine = async () => {
    const res = await api.get('/matchmaking/me');
    setMine(res.data.items || []);
  };
  useEffect(()=>{ loadAll(); }, [q, filters.game, filters.platform, filters.region, filters.status]);
  useEffect(()=>{ loadMine(); }, []);

  const resetForm = () => setForm({ game: '', mode: '', platform: '', region: '', languages: '', skill: '', availability: '', voice: true, crossplay: true, tags: '', description: '', status: 'open' });

  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...form, languages: form.languages ? form.languages.split(',').map(s=>s.trim()).filter(Boolean) : [], tags: form.tags ? form.tags.split(',').map(s=>s.trim()).filter(Boolean) : [] };
    if (editing) {
      await api.put(`/matchmaking/${editing.id}`, payload);
    } else {
      await api.post('/matchmaking', payload);
    }
    resetForm();
    setEditing(null);
    await Promise.all([loadAll(), loadMine()]);
  };

  const onEdit = (p) => {
    setEditing(p);
    setForm({
      game: p.game || '',
      mode: p.mode || '',
      platform: p.platform || '',
      region: p.region || '',
      languages: Array.isArray(p.languages) ? p.languages.join(', ') : '',
      skill: p.skill || '',
      availability: p.availability || '',
      voice: !!p.voice,
      crossplay: !!p.crossplay,
      tags: Array.isArray(p.tags) ? p.tags.join(', ') : '',
      description: p.description || '',
      status: p.status || 'open',
    });
  };
  const onDelete = async (p) => {
    if (!confirm('Eintrag wirklich löschen?')) return;
    await api.delete(`/matchmaking/${p.id}`);
    if (editing && editing.id === p.id) { setEditing(null); resetForm(); }
    await Promise.all([loadAll(), loadMine()]);
  };

  const Card = ({ p, mine }) => {
    const [rawgImg, setRawgImg] = useState(null);
    const [localIconFailed, setLocalIconFailed] = useState(false);
    useEffect(() => {
      let mounted = true;
      const run = async () => {
        if (!p?.game) return;
        // Try RAWG only after local icon failed
        if (localIconFailed) {
          const url = await fetchRawgImageUrl(p.game);
          if (mounted) setRawgImg(url || null);
        }
      };
      run();
      return () => { mounted = false; };
    }, [p?.game, localIconFailed]);
    return (
    <li style={{ background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:10, padding:12, marginBottom:10 }}>
      <div style={{ display:'flex', justifyContent:'space-between', gap:12 }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          {/* Kopfzeile mit Game-Icon, Titel und Meta */}
          <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
            {/* Game-Cover/Icon in doppelter Breite mit korrektem Seitenverhältnis */}
            <div title={p.game} style={{ width:112, display:'inline-block', lineHeight:0 }}>
              {/* 1) Lokales Icon (SVG/PNG). Bei Fehler → RAWG versuchen */}
              {!localIconFailed && (
                <img
                  src={getGameIconPath(p.game)}
                  alt={p.game}
                  style={{ width:112, height:'auto', maxHeight:'none', borderRadius:8, display:'block', boxShadow:'0 1px 2px rgba(0,0,0,0.35)' }}
                  onError={()=> setLocalIconFailed(true)}
                />
              )}
              {/* 2) RAWG Cover (nur wenn lokales Icon fehlgeschlagen ist) */}
              {localIconFailed && rawgImg && (
                <img
                  src={rawgImg}
                  alt={p.game}
                  style={{ width:112, height:'auto', maxHeight:'none', borderRadius:8, display:'block', boxShadow:'0 1px 2px rgba(0,0,0,0.35)' }}
                  onError={() => setRawgImg(null)}
                />
              )}
              {/* 3) Fallback-Badge, falls kein Bild */}
              {localIconFailed && !rawgImg && (
                <div style={{ width:112, height:63, borderRadius:8, background: gameColor(p.game).bg, color: gameColor(p.game).fg, display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700, fontSize:18 }}>
                  {gameInitials(p.game)}
                </div>
              )}
            </div>
            <b style={{ fontSize:16, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{p.game}</b>
            {p.mode && <span style={{ fontSize:12, color:'#bbb' }}>· {p.mode}</span>}
            {p.platform && <span style={{ fontSize:12, color:'#bbb' }}>· {p.platform}</span>}
            {p.region && <span style={{ fontSize:12, color:'#bbb' }}>· {p.region}</span>}
            <span style={{ fontSize:12, color: p.status==='open'?'#5bd65b':'#f0ad4e' }}>· {p.status==='open'?'offen':'geschlossen'}</span>
          </div>
          {p.description && <div style={{ marginTop:6 }}>{p.description}</div>}
          <div style={{ marginTop:6, fontSize:12, color:'#bbb' }}>
            {p.languages?.length ? <>Sprachen: {p.languages.join(', ')} · </> : null}
            {p.skill ? <>Skill: {p.skill} · </> : null}
            {p.availability ? <>Zeiten: {p.availability} · </> : null}
            {p.voice ? <>Voice: ja · </> : <>Voice: nein · </>}
            {p.crossplay ? <>Crossplay: ja</> : <>Crossplay: nein</>}
          </div>
          {Array.isArray(p.tags) && p.tags.length>0 && (
            <div style={{ marginTop:6, display:'flex', flexWrap:'wrap', gap:6 }}>
              {p.tags.map(t => <span key={t} style={{ fontSize:11, padding:'2px 6px', borderRadius:12, border:'1px solid rgba(255,255,255,0.15)' }}>#{t}</span>)}
            </div>
          )}
          {/* Author-Zeile mit Avatar */}
          <div style={{ marginTop:8, fontSize:12, color:'#aaa', display:'flex', alignItems:'center', gap:8 }}>
            <AvatarImg src={p.User?.avatarUrl || '/avatars/default-avatar.png'} alt="avatar" size={24} style={{ border:'1px solid rgba(255,255,255,0.2)' }} />
            <span>von {p.User?.displayName || p.User?.username || `User #${p.userId}`}</span>
            <span>· {new Date(p.createdAt).toLocaleString()}</span>
          </div>
        </div>
        <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:6 }}>
          <button onClick={()=>setContactUser({ id: p.userId, name: p.User?.displayName || p.User?.username || `User #${p.userId}` })}>Kontakt aufnehmen</button>
          {mine && (
            <>
              <button onClick={()=>onEdit(p)}>Bearbeiten</button>
              <button onClick={()=>onDelete(p)} style={{ marginLeft: 6, color: 'red' }}>Löschen</button>
            </>
          )}
        </div>
      </div>
    </li>
    );
  };

  return (
    <div className="home-page">
      <section style={{ width:'100vw', margin:0, padding:0, position:'relative', left:'-120px' }}>
        <h2 className="hero-title" style={{ textAlign: 'center', margin:'0 0 8px' }}>Mitspieler finden</h2>
        <p className="hero-desc" style={{ margin:'0 auto 24px', textAlign: 'center', maxWidth:760 }}>Finde die perfekten Gaming-Partner – wir bringen dich mit passenden Mitspielern zusammen.</p>
      </section>
      <section style={{ width:'100%', maxWidth:1100, margin:'0 auto', padding:'0 16px 40px 16px', color:'#fff' }}>
        <div style={{ display:'grid', gridTemplateColumns:'1.2fr 1fr', gap:16 }}>
          <div>
            <h3 style={{ margin:'8px 0' }}>Alle Einträge</h3>
            <div style={{ background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:10, padding:12, marginBottom:10 }}>
              <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                <input placeholder="Suche (Spiel/Modus/Tags)" value={q} onChange={e=>setQ(e.target.value)} />
                <input placeholder="Spiel" value={filters.game} onChange={e=>setFilters({...filters, game: e.target.value})} />
                <input placeholder="Plattform" value={filters.platform} onChange={e=>setFilters({...filters, platform: e.target.value})} />
                <input placeholder="Region" value={filters.region} onChange={e=>setFilters({...filters, region: e.target.value})} />
                <select value={filters.status} onChange={e=>setFilters({...filters, status: e.target.value})}>
                  <option value="open">offen</option>
                  <option value="closed">geschlossen</option>
                </select>
              </div>
            </div>
            <ul style={{ marginTop:8 }}>
              {list.map(p => <Card key={p.id} p={p} mine={p.userId===user?.id} />)}
              {list.length===0 && <li style={{ color:'#aaa', fontSize:12 }}>Keine Einträge gefunden.</li>}
            </ul>
          </div>
          <div>
            <h3 style={{ margin:'8px 0' }}>Mein Eintrag</h3>
            <div style={{ background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:10, padding:12 }}>
              <form onSubmit={submit}>
                <div style={{ display:'grid', gap:8 }}>
                  <label>
                    <div style={{ fontSize:12, color:'#bbb' }}>Spiel</div>
                    <input required value={form.game} onChange={e=>setForm({...form, game: e.target.value})} placeholder="z. B. Apex Legends" />
                  </label>
                  <label>
                    <div style={{ fontSize:12, color:'#bbb' }}>Variante/Modus (optional)</div>
                    <input value={form.mode} onChange={e=>setForm({...form, mode: e.target.value})} placeholder="Ranked, Duos, etc." />
                  </label>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
                    <label>
                      <div style={{ fontSize:12, color:'#bbb' }}>Plattform</div>
                      <input value={form.platform} onChange={e=>setForm({...form, platform: e.target.value})} placeholder="PC, PS5, Xbox..." />
                    </label>
                    <label>
                      <div style={{ fontSize:12, color:'#bbb' }}>Region</div>
                      <input value={form.region} onChange={e=>setForm({...form, region: e.target.value})} placeholder="EU, DE, NA..." />
                    </label>
                  </div>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
                    <label>
                      <div style={{ fontSize:12, color:'#bbb' }}>Sprachen (Komma-getrennt)</div>
                      <input value={form.languages} onChange={e=>setForm({...form, languages: e.target.value})} placeholder="de, en" />
                    </label>
                    <label>
                      <div style={{ fontSize:12, color:'#bbb' }}>Skill</div>
                      <input value={form.skill} onChange={e=>setForm({...form, skill: e.target.value})} placeholder="Casual, Ranked Gold, K/D, etc." />
                    </label>
                  </div>
                  <label>
                    <div style={{ fontSize:12, color:'#bbb' }}>Verfügbarkeit</div>
                    <input value={form.availability} onChange={e=>setForm({...form, availability: e.target.value})} placeholder="z. B. abends 19-22 Uhr" />
                  </label>
                  <div>
                    <label style={{ marginRight:12 }}><input type="checkbox" checked={form.voice} onChange={e=>setForm({...form, voice: e.target.checked})} /> Voice bevorzugt</label>
                    <label><input type="checkbox" checked={form.crossplay} onChange={e=>setForm({...form, crossplay: e.target.checked})} /> Crossplay ok</label>
                  </div>
                  <label>
                    <div style={{ fontSize:12, color:'#bbb' }}>Tags (Komma-getrennt)</div>
                    <input value={form.tags} onChange={e=>setForm({...form, tags: e.target.value})} placeholder="chill, ranked, anfängerfreundlich" />
                  </label>
                  <label>
                    <div style={{ fontSize:12, color:'#bbb' }}>Beschreibung</div>
                    <textarea value={form.description} onChange={e=>setForm({...form, description: e.target.value})} placeholder="Kurze Beschreibung, was du suchst..." />
                  </label>
                  <label>
                    <div style={{ fontSize:12, color:'#bbb' }}>Status</div>
                    <select value={form.status} onChange={e=>setForm({...form, status: e.target.value})}>
                      <option value="open">offen</option>
                      <option value="closed">geschlossen</option>
                    </select>
                  </label>
                  <div>
                    <button type="submit">{editing ? 'Eintrag aktualisieren' : 'Eintrag erstellen'}</button>
                    {editing && <button type="button" onClick={()=>{ setEditing(null); resetForm(); }} style={{ marginLeft:8 }}>Abbrechen</button>}
                  </div>
                </div>
              </form>
            </div>
            <h4 style={{ margin:'12px 0 6px' }}>Meine Einträge</h4>
            <ul>
              {mine.map(p => <Card key={p.id} p={p} mine />)}
              {mine.length===0 && <li style={{ color:'#aaa', fontSize:12 }}>Du hast noch keine Einträge.</li>}
            </ul>
          </div>
        </div>
      </section>
      {contactUser && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center' }} onClick={()=>setContactUser(null)}>
          <div style={{ background:'#222', color:'#fff', padding:16, borderRadius:12, width:600 }} onClick={e=>e.stopPropagation()}>
            <h3>Nachricht an {contactUser.name}</h3>
            <MessageSendForm recipientId={contactUser.id} recipientName={contactUser.name} onSent={(r)=>{ if(r?.success){ setTimeout(()=>setContactUser(null), 800); } }} />
            <div style={{ textAlign:'right' }}>
              <button onClick={()=>setContactUser(null)}>Schließen</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
